import { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { createPortal } from 'react-dom';
import { PhotoInfo } from '../api/types';
import BlurFade from './BlurFade';
import { useLanguage } from '../i18n/LanguageContext';

interface PhotoGridProps {
  photos: PhotoInfo[];
  baseDelay?: number;
}

function PhotoGrid({ photos, baseDelay = 0 }: PhotoGridProps) {
  const { t, lang } = useLanguage();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const close = useCallback(() => setActiveIndex(null), []);
  const showPrev = useCallback(
    () => setActiveIndex((i) => (i === null ? i : (i - 1 + photos.length) % photos.length)),
    [photos.length]
  );
  const showNext = useCallback(
    () => setActiveIndex((i) => (i === null ? i : (i + 1) % photos.length)),
    [photos.length]
  );

  useEffect(() => {
    if (activeIndex === null) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') showPrev();
      if (e.key === 'ArrowRight') showNext();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [activeIndex, close, showPrev, showNext]);

  const activePhoto = activeIndex !== null ? photos[activeIndex] : null;
  const activeCaption = activePhoto ? (lang === 'en' ? activePhoto.caption_en : activePhoto.caption_tr) : '';

  return (
    <>
      <div className="photos-grid">
        {photos.map((photo, index) => (
          <BlurFade key={photo.id} delay={baseDelay + index * 0.05} className="photos-item">
            <button
              type="button"
              className="photos-thumb-btn"
              onClick={() => setActiveIndex(index)}
              aria-label={(lang === 'en' ? photo.caption_en : photo.caption_tr) || ''}
            >
              <img
                src={photo.image_url}
                alt={(lang === 'en' ? photo.caption_en : photo.caption_tr) || ''}
                className={index % 3 === 1 ? 'aspect-portrait' : 'aspect-square'}
                loading="lazy"
                decoding="async"
              />
            </button>
          </BlurFade>
        ))}
      </div>

      {activePhoto &&
        createPortal(
          <div className="photos-lightbox" onClick={close}>
            <button type="button" className="photos-lightbox-close" onClick={close} aria-label="Close">
              <X size={20} />
            </button>
            {photos.length > 1 && (
              <button
                type="button"
                className="photos-lightbox-nav photos-lightbox-prev"
                onClick={(e) => { e.stopPropagation(); showPrev(); }}
                aria-label={t.blog.previous}
              >
                <ChevronLeft size={22} />
              </button>
            )}
            <div className="photos-lightbox-content" onClick={(e) => e.stopPropagation()}>
              <img src={activePhoto.image_url} alt={activeCaption || ''} />
              {activeCaption && <p className="photos-lightbox-caption">{activeCaption}</p>}
            </div>
            {photos.length > 1 && (
              <button
                type="button"
                className="photos-lightbox-nav photos-lightbox-next"
                onClick={(e) => { e.stopPropagation(); showNext(); }}
                aria-label={t.blog.next}
              >
                <ChevronRight size={22} />
              </button>
            )}
          </div>,
          document.body
        )}
    </>
  );
}

export default PhotoGrid;
